import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react'
import { CheckCircle2 } from 'lucide-react'

type ToastItem = { id: string; message: string; closing: boolean }

const DURATION_MS = 3500
const EXIT_MS = 150

const ToastContext = createContext<(message: string) => void>(() => {})

/**
 * Jedna fronta krátkých potvrzení pro celou appku („Uloženo", „Načteno…").
 * Na rozdíl od Alertu nezabírá místo v layoutu a nezavírá se ručně — objeví
 * se, po pár sekundách zmizí. Bez Radixu: @radix-ui/react-toast se pod
 * React 19 zavíral hned po namountování (onOpenChange(false) okamžitě po
 * onOpenChange(true)), a vlastní fronta je pro tenhle jednoduchý případ
 * stejně jednodušší než to ladit.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([])
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>())

  const dismiss = useCallback((id: string) => {
    setItems((prev) => prev.map((t) => (t.id === id ? { ...t, closing: true } : t)))
    timers.current.set(
      id,
      setTimeout(() => setItems((prev) => prev.filter((t) => t.id !== id)), EXIT_MS),
    )
  }, [])

  const toast = useCallback((message: string) => {
    const id = crypto.randomUUID()
    setItems((prev) => [...prev, { id, message, closing: false }])
    timers.current.set(id, setTimeout(() => dismiss(id), DURATION_MS))
  }, [dismiss])

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <ol className="pointer-events-none fixed bottom-4 right-4 z-[100] flex w-[min(22rem,calc(100vw-2rem))] flex-col gap-2 sm:bottom-6 sm:right-6">
        {items.map((item) => (
          <li
            key={item.id}
            role="status"
            data-state={item.closing ? 'closed' : 'open'}
            className="toast pointer-events-auto flex items-center gap-2 rounded-[var(--radius-lg)] border border-[var(--border)] bg-[var(--card)] px-4 py-3 text-[length:var(--text-sm)] text-[var(--foreground)] shadow-xl"
          >
            <CheckCircle2 size={16} aria-hidden className="shrink-0 text-[var(--rule-ok)]" />
            <span className="flex-1">{item.message}</span>
          </li>
        ))}
      </ol>
    </ToastContext.Provider>
  )
}

/** Vrací funkci `toast(zpráva)`. Mimo `ToastProvider` je to no-op, ne pád. */
export const useToast = () => useContext(ToastContext)
